import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form, TextArea } from "semantic-ui-react";
import { apiV2Server } from "../../config";
import LanguageButton from "../../components/nav/Links/LanguageButton";
import { Parsers } from "../../helpers/formFields";
import { getLanguage } from "../../helpers/getLocationLanguage";
import { CircularProgress, Grid } from "@material-ui/core";
import { ParcelStatusHistory } from "../../components/Parcel/tracking/ParcelTrackingHistory";
import { ResultsList } from "../../components/Parcel/tracking/ResultsList";
import { TrackingStepsBar } from "../../components/Parcel/tracking/TrackingStepsBar";
import NoticeMessage from './NoticeMessage';
import '../../assets/styles/trackingV2.scss';

const TrackingV2 = ({ location, history, match }) => {
  const [processSteps, setProcessSteps] = useState(null);
	const [xltID, setXltID] = useState("");
	const [xltArray, setXltArray] = useState([]);
	const language = useMemo(() => getLanguage(location), [location]);
	const [parcelsTrackingResults, setParcelsTrackingResults] = useState(null);
	const [activeParcel, setActiveParcel] = useState(null);
	const { t } = useTranslation("tracking");
	const [submitted, setSubmitted] = useState(false);
	const [pudoPoint, setPudoPoint] = useState(null);

  const fetchTrackingData = (xlt, lang) => {
		setSubmitted(true);
    const uri = encodeURI(`${apiV2Server}parcels/tracking/${xlt}?lang=${lang}&showOrder=true`);
		fetch(uri)
			.then((res) => res.json().then((data) => {
				setParcelsTrackingResults(data);
				setActiveParcel({...data[0], index: 0});
				setPudoPoint(data.pudoPoint);
				setSubmitted(false);
			}))
			.catch((err) => {
				console.log(`Error on request to get parcel ${xlt} tracking info`, err);
				setPudoPoint(null);
				setSubmitted(false);
			});
	};

	const getUrlParamsAndFetchData = () => {
		const foundXLTIDs = [...(new Set((match.params.xltID && Parsers.sanitizeString(match.params.xltID).replace(/\s/g, '').split(",")) || null))];
		if (foundXLTIDs && foundXLTIDs.length) {
			setXltID(foundXLTIDs.join("\n"));
			setXltArray(foundXLTIDs);
			fetchTrackingData(foundXLTIDs, language);
		} else {
			setPudoPoint(null);
		}
	};

	const selectResultItem = (activeIndex) => {
		setActiveParcel({...parcelsTrackingResults[activeIndex], index: activeIndex});
	};

	useEffect(() => {
		getUrlParamsAndFetchData();
	}, [location.search, match]);

  useEffect(() => {
    fetch(`${apiV2Server}parcels/tracking/processSteps`)
			.then((res) => res.json().then((data) => setProcessSteps(data)))
			.catch((err) => console.error('Error fetching process steps', err));
  }, []);

	const handleTrackingCodeChange = (e, { value }) => {
		const codes =
			(value &&
				value
					.split(/(?:,|\n|\s)+/)
					.map((v) => Parsers.sanitizeString(v))
					.filter((v) => !!v )) ||
			[];
		setXltArray([...(new Set(codes))]);
		setXltID(value || "");
	};

  const uri = encodeURI(`/tracking/${xltArray.join(",")}?lang=${language}`);
	const onSetLocationPathname = useCallback(() => {
		history.push(uri);
	}, [history, xltID, language]);

  const handleBackButtonClick = (e) => {
    e.preventDefault();
    setActiveParcel(null);
    setParcelsTrackingResults(null);
    setSubmitted(false);
  }
  
	return (
		<>
			<div id="trackingV2" className={language}>
        <LanguageButton location={location} history={history} language={language} />
        <Grid container justify="center"  spacing={1}>
          {parcelsTrackingResults && parcelsTrackingResults.length > 0 && (
            <>
            <a href="#" className="back-button" onClick={handleBackButtonClick}>{t('back')}</a>
              <Grid item xs={12} sm={12} md={3} lg={3} className="results-section">
                <ResultsList items={parcelsTrackingResults} activeParcel={activeParcel} selectResultItem={selectResultItem} />
              </Grid>
              <Grid container item xs={12} sm={12} md={8} lg={8} justify="center">        
                <Grid item xs={2} sm={12} md={11}>
                  {activeParcel?.statusHistory && <TrackingStepsBar statusHistory={activeParcel.statusHistory} processSteps={processSteps} deliveryMethod={activeParcel.deliveryMethod} isReturn={activeParcel.isReturn} />}
                </Grid>
                <Grid item xs={10} sm={12} md={10}>
                  {activeParcel && activeParcel.publicRemark && (
                    <div className="public-remark text-center">
                      <h3 className="section-title">{t("yourAttention")}</h3>
                      <p className="remark-text">{activeParcel.publicRemark}</p>
                      <div className="contact-link">
                        {t("toContactCustomerService")}
                        <a href={language === "he" ? "https://www.exelot.com/he/contact/#form_he" : "https://www.exelot.com/contact/"} target="_blank" rel="noopener noreferrer">
                          {t("here")}
                        </a>
                      </div>
                    </div>
                  )}
                  {activeParcel && activeParcel.orderID && (
                    <div className="order-id">
                      <p className="heading-order-number">{t("orderID")} {activeParcel.orderID}</p>
                    </div>
                  )}
                  {activeParcel && activeParcel.hawb && (
                    <div className="order-id">
                      <p className="heading-order-number">{t("trackingNo")} {activeParcel.hawb}</p>
                    </div>
                  )}
                  {activeParcel?.pudoPoint && (
                    <div className="pudo-point">
                      <h3 className="title bold text-center">{t(activeParcel.pudoPoint.pudoType)}:</h3>
                      <div className="box pudo-point-data">
                        <div>
                          {t("name")} {activeParcel.pudoPoint.name}
                        </div>
                        <div>
                          {t("address")} {activeParcel.pudoPoint.address.stAddress} {activeParcel.pudoPoint.address.houseNumber}, {activeParcel.pudoPoint.address.city}
                        </div>
                        <div>
                          {t("additionalInformation")} {activeParcel.pudoPoint.additionalInformation}
                        </div>
                        {/* <div>
                          {t("distributedBy")} {t(activeParcel.pudoPoint.lastMileProvider)}
                        </div> */}
                      </div>
                    </div>
                  )}
                  {activeParcel?.statusHistory && <ParcelStatusHistory statusHistory={activeParcel.statusHistory} />}
                </Grid>
              </Grid>
            </>
          )}
          {parcelsTrackingResults && parcelsTrackingResults.length === 0 && (
            <Grid item xs={12} sm={9}>
            <div className="no-results">
              <h1>{t('noResultsFound')}</h1>
              <a href="#" className="back-button" onClick={handleBackButtonClick}>{t('back')}</a>
            </div>
            </Grid>
          )}
          {!parcelsTrackingResults && (
            <Grid item xs={12} sm={6}>
              <Form className="tracking-form" onSubmit={onSetLocationPathname}>
                <Form.Field>
                  <label htmlFor="xltID" className="codes-input-label">
                    {t("trackingMultipleCodes")}{" "}
                  </label>
                  <TextArea rows={4} className="codes-textarea" name="xltID" required placeholder={t("enterOneCodePerLine")} id="xltID" value={xltID} onChange={handleTrackingCodeChange} style={{ direction: "ltr" }} />
                </Form.Field>
                <div className="centered">
                  <button className="btn small orange relative" type="submit" disabled={submitted || !xltArray.length}>
                    {t("trackButtonText")} {xltArray.length > 0 && `(${xltArray.length})`}
                    {submitted && !parcelsTrackingResults && <CircularProgress size={20} color="inherit" className="loading-indicator" />}
                  </button>
                </div>
              </Form>
            </Grid>
          )}
          <Grid item xs={12}>
            <div className={`additional-information `}>
              <div className="text-link">
                {t("forMoreDetails")}
                <a href="https://www.exelot.com/he/support/" target="_blank" rel="noopener noreferrer">
                  {t("here")}
                </a>
              </div>
              {t("weAreProud")}
              <br />
              {t("exelotIts")}
            </div>
          </Grid>
        </Grid>
        {false && language === 'he' && <NoticeMessage />}
      </div>
		</>
	);
};

export default TrackingV2;
